import { useCallback, useEffect, useRef, useState } from "react";
import { tripsApi, type CheckStatus } from "@/lib/api";

export type CheckPhase = "idle" | "checking" | "done" | "error";

export interface CheckPricesState {
  phase: CheckPhase;
  /** Live job status while polling / after completion. */
  status: CheckStatus | null;
  /** Human-friendly line for the inline feedback strip. */
  message: string;
  /** Kick off a check; resolves immediately (polling continues in background). */
  start: () => void;
}

const POLL_MS = 3000;
/**
 * Safety stop so a stuck job can't poll forever. A check fans out one Playwright
 * scrape per route with ~5s pacing AND each route can take ~60s when a source
 * times out — a 4-route trip routinely runs 3-4 minutes. The old 60-poll (180s)
 * budget cut off BEFORE such trips finished, and the cutoff path never refetched,
 * so freshly-landed fares showed as "no options". 120 polls = 6 min covers the
 * realistic worst case.
 */
const MAX_POLLS = 120;
/**
 * While a check is running, refetch the trip's fares every Nth poll so options
 * appear as each route finishes — the backend stores per-route as it goes — even
 * if the live price:update socket event never arrives (e.g. room mismatch).
 */
const REFETCH_EVERY_POLLS = 3;

/**
 * B2 "never leave the user with silence" — drives the async Check Prices flow:
 * POST /check → poll /check-status every 3s → surface progress and a final
 * result, then call `onComplete` so the page can refresh fares/plan data.
 */
export function useCheckPrices(
  tripId: string,
  onComplete?: () => void,
  /**
   * A jobId for a check the SERVER already started (e.g. on trip creation). When
   * provided, the hook resumes polling it on mount — the page shows live
   * "Checking fares…" and renders fares the moment they land, with no click.
   */
  initialJobId?: string | null,
): CheckPricesState {
  const [phase, setPhase] = useState<CheckPhase>(initialJobId ? "checking" : "idle");
  const [status, setStatus] = useState<CheckStatus | null>(null);
  const [message, setMessage] = useState(initialJobId ? "Checking fares…" : "");

  const timerRef = useRef<number | null>(null);
  const pollsRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  // Stop polling if the component unmounts mid-check.
  useEffect(() => clearTimer, [clearTimer]);

  const poll = useCallback(
    async (jobId: string) => {
      pollsRef.current += 1;
      try {
        const s = await tripsApi.checkStatus(tripId, jobId);
        setStatus(s);

        if (s.status === "running") {
          setMessage(
            s.checkedRoutes > 0
              ? `Checking fares… ${s.checkedRoutes} route${s.checkedRoutes === 1 ? "" : "s"} so far`
              : "Checking fares…",
          );
          // Surface fares as routes finish — don't make the user wait for the
          // whole job. Cheap: react-query dedupes and only refetches stale data.
          if (pollsRef.current % REFETCH_EVERY_POLLS === 0) {
            onCompleteRef.current?.();
          }
          if (pollsRef.current >= MAX_POLLS) {
            setPhase("done");
            setMessage("Still working — we'll keep tracking and update your plan.");
            // Even on the safety cutoff, pull whatever DID land so the user sees
            // options instead of an empty plan.
            onCompleteRef.current?.();
            return;
          }
          timerRef.current = window.setTimeout(() => poll(jobId), POLL_MS);
          return;
        }

        // Terminal states.
        // "error" is what the server actually sends; this compared against
        // "failed" and so never matched — a blown-up check reported itself as
        // "no fares found yet". See the CheckStatus note in lib/api.
        if (s.status === "error") {
          setPhase("error");
          setMessage(s.message || "Price check failed. Please try again.");
          return;
        }

        setPhase("done");
        if (s.status === "completed" && s.flightsFound > 0) {
          setMessage(
            `Found ${s.flightsFound} fare${s.flightsFound === 1 ? "" : "s"} across ${s.checkedRoutes} route${s.checkedRoutes === 1 ? "" : "s"}.`,
          );
        } else {
          setMessage("No fares found yet — we'll keep tracking and let you know.");
        }
        onCompleteRef.current?.();
      } catch {
        setPhase("error");
        setMessage("Couldn't read price-check progress. Please try again.");
      }
    },
    [tripId],
  );

  const start = useCallback(() => {
    if (phase === "checking") return;
    clearTimer();
    pollsRef.current = 0;
    setStatus(null);
    setPhase("checking");
    setMessage("Starting fare check…");
    tripsApi
      .check(tripId)
      .then((res) => {
        if (!res?.jobId) {
          setPhase("error");
          setMessage("Couldn't start the price check. Please try again.");
          return;
        }
        setMessage("Checking fares…");
        timerRef.current = window.setTimeout(() => poll(res.jobId), POLL_MS);
      })
      .catch(() => {
        setPhase("error");
        setMessage("Couldn't start the price check. Please try again.");
      });
  }, [phase, tripId, clearTimer, poll]);

  // Resume a server-started job (initialJobId) once, on mount: begin polling
  // straight away so the user lands on a page that's already "Checking fares…".
  const resumedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!initialJobId || resumedRef.current === initialJobId) return;
    resumedRef.current = initialJobId;
    clearTimer();
    pollsRef.current = 0;
    setPhase("checking");
    setMessage("Checking fares…");
    timerRef.current = window.setTimeout(() => poll(initialJobId), POLL_MS);
  }, [initialJobId, poll, clearTimer]);

  return { phase, status, message, start };
}
